import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, TablePagination } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../Context/AuthProvider";
import { getOrderColumns, getBondCharColumns, getBondLadderColumns, LoadingOverlay } from "../Component/OrderComponents/columnConfig.js";
import OrderPageHeader from "../Component/OrderComponents/OrderPageHeader.jsx";
import BondTable from "../Component/BondTable.jsx";
import StagingBlotterTable from "../Component/StagingBlotterTable.jsx";
import OrderEntry from "../Component/OrderEntry.jsx";
import CustomizeColumns from "../Component/CustomizeColumns.jsx";
import { useBondLadderStore } from "../Store/bondLadderStore.js";
import { useBondCharacteristicStore } from "../Store/bondCharacteristicStore.js";
import { useActualOrderStore } from "../Store/actualOrderStore.js";
import { defaultMktxDealers } from "../Component/OrderEntry/marketAxessDealers";
import { processBondData, getActiveData } from "../Component/OrderComponents/utils.js";
import { useOrders } from "../Component/OrderComponents/UserOrder.jsx";
import { useStagingOrders } from "../Component/OrderComponents/UseStagingOrder.jsx";
import { useBondCharacteristics } from "../Component/OrderComponents/UseBondCharacteristics.jsx";
import { useBondLadders } from "../Component/OrderComponents/UseBondLadder.jsx";
import { useWebSocketOrderUpdates } from "../Component/OrderComponents/useWebSocketUpdates.jsx";
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../Utils/columnVersionManager"; // Import the utility
import { getPreference } from "../Utils/userPreferenceManger"; // Import user preference manager
import { USER_ROLES, PROTOCOL_TYPES, MKTX_DEALER_LIST_MODE_SUBMIT_TO_DISPLAY, MKTX_PRICING_PROCESSES, MKTX_PRICING_PROCESS_DISPLAY, ORDER_ENTRY_SECONDARY_SOURCES } from "../Constants/AppConstants";
import * as XLSX from 'xlsx';
import { usePageSubscription } from "../Utils/WebSocketProvider.jsx";
import { countOrdersBlotterRows } from "../Utils/ordersBlotterUpdates";

const MULTI_VENUE_PROTOCOLS = new Set([
  PROTOCOL_TYPES.RFQ,
  PROTOCOL_TYPES.TRUMID_RFQ,
  PROTOCOL_TYPES.MARKETAXESS,
]);

const VALID_MKTX_PRICING_PROCESS_VALUES = new Set(Object.values(MKTX_PRICING_PROCESSES));
const MKTX_PRICING_PROCESS_DISPLAY_LOOKUP = Object.entries(MKTX_PRICING_PROCESS_DISPLAY).reduce((acc, [submitValue, displayValue]) => {
  if (displayValue) {
    acc[displayValue.toUpperCase()] = submitValue;
  }
  return acc;
}, {});

const normalizeMktxPricingProcessValue = (value) => {
  if (value == null) return value;
  const trimmed = String(value).trim();
  if (!trimmed) return trimmed;
  const upperValue = trimmed.toUpperCase();
  if (VALID_MKTX_PRICING_PROCESS_VALUES.has(upperValue)) {
    return upperValue;
  }
  return MKTX_PRICING_PROCESS_DISPLAY_LOOKUP[upperValue] || trimmed;
};

const normalizeVenueValue = (protocol, venue) => {
  if (!MULTI_VENUE_PROTOCOLS.has(protocol)) {
    return venue ?? "";
  }
  if (!venue) return [];
  if (Array.isArray(venue)) return venue;
  if (typeof venue === "string") {
    return venue
      .split(",")
      .map((val) => val.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeOrderForForm = (order) => {
  if (!order) return order;
  const protocol = order.protocol;
  return {
    ...order,
    qty: order.qty ?? order.quantity ?? "",
    venue: normalizeVenueValue(protocol, order.venue),
    mktxDealerListMode:
      MKTX_DEALER_LIST_MODE_SUBMIT_TO_DISPLAY[order.mktxDealerListMode] ||
      order.mktxDealerListMode ||
      null,
    mktxPricingProcess: normalizeMktxPricingProcessValue(order.mktxPricingProcess) ?? null,
      expiryTime: order.expiryTime || order.rfqExpiryTime || null,

  };
};

const OrderPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const userRole = localStorage.getItem("role");
  usePageSubscription("orders");

  // Modified URL parameter handling
  const mainTabParam = searchParams.get('view') || '0';
  const subTabParam = searchParams.get('tab') || '0';

  const initialMainTab = (() => {
    const parsed = parseInt(mainTabParam);
    if (isNaN(parsed) || parsed < 0 || parsed > 1) {
      navigate('/order?view=0&tab=0', { replace: true });
      return 0;
    }
    return parsed;
  })();

  // For orders view (mainTab===1), there is only sub-tab index 0.
  // For staging view (mainTab===0), valid sub-tab indexes are 0, 1, 2.
  const initialActiveTab = (() => {
    const parsed = parseInt(subTabParam);
    const maxAllowedTab = initialMainTab === 1 ? 0 : 2;
    if (isNaN(parsed) || parsed < 0 || parsed > maxAllowedTab) {
      navigate(`/order?view=${initialMainTab}&tab=0`, { replace: true });
      return 0;
    }
    return parsed;
  })();

  const [mainTab, setMainTab] = useState(initialMainTab);
  const [activeTab, setActiveTab] = useState(initialActiveTab);

  // Add this new effect to react to URL param changes
  useEffect(() => {
    const newMainTab = parseInt(mainTabParam);
    const newSubTab = parseInt(subTabParam);
    
    // Update state if URL parameters change
    if (!isNaN(newMainTab) && newMainTab >= 0 && newMainTab <= 1) {
      setMainTab(newMainTab);
    }
    
    // For orders view (mainTab===1), only sub-tab 0 is valid
    // For staging view (mainTab===0), valid sub-tabs are 0, 1, 2
    const maxAllowedTab = newMainTab === 1 ? 0 : 2;
    if (!isNaN(newSubTab) && newSubTab >= 0 && newSubTab <= maxAllowedTab) {
      setActiveTab(newSubTab);
    }
  }, [mainTabParam, subTabParam]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Replace single sortConfig with a map of configs for each tab combination with default values
  const [sortConfigs, setSortConfigs] = useState({
    '0-0': { key: "updatedAt", direction: "desc" },     // Staging orders default
    '0-1': { key: "characteristicMstId", direction: "desc" }, // Bond characteristics default
    '0-2': { key: "bondLadderId", direction: "desc" },  // Bond ladder default
    '1-0': { key: "updatedAt", direction: "desc" },     // Orders default
  });

  // Get current sort config based on active tabs
  const getCurrentSortConfig = () => {
    return sortConfigs[`${mainTab}-${activeTab}`] || { key: "", direction: "" };
  };

  // Track visited tab combinations to avoid redundant API calls
  const [visitedTabs, setVisitedTabs] = useState(new Set());

  // Data hooks with pagination and current tab's sorting parameters
  const { 
    orderData, 
    setOrderData, 
    totalElements: orderTotalElements, 
    setTotalElements: setOrderTotalElements,
    loading: loadingOrders 
  } = useOrders(
    mainTab === 1 ? token : null, 
    page, 
    rowsPerPage, 
    getCurrentSortConfig().key, 
    getCurrentSortConfig().direction,
    visitedTabs,
    setVisitedTabs,
    `1-0` // Tab identifier for orders
  );
  
  const { 
    stagingOrderData, 
    setStagingOrderData, 
    totalElements: stagingOrderTotalElements,
    setTotalElements: setStagingOrderTotalElements, 
    loading: loadingStagingOrders 
  } = useStagingOrders(
    mainTab === 0 && activeTab === 0 ? token : null, 
    page, 
    rowsPerPage, 
    getCurrentSortConfig().key, 
    getCurrentSortConfig().direction,
    visitedTabs,
    setVisitedTabs,
    `0-0` // Tab identifier for staging orders
  );
  
  const { 
    bondCharData, 
    setBondCharData, 
    totalElements: charTotalElements,
    loading: loadingBondChar 
  } = useBondCharacteristics(
    mainTab === 0 && activeTab === 1 ? token : null, 
    page, 
    rowsPerPage, 
    getCurrentSortConfig().key, 
    getCurrentSortConfig().direction,
    visitedTabs,
    setVisitedTabs,
    `0-1` // Tab identifier for bond characteristics
  );
  
  const { 
    bondLadderData, 
    setBondLadderData, 
    totalElements: ladderTotalElements,
    loading: loadingBondLadder 
  } = useBondLadders(
    mainTab === 0 && activeTab === 2 ? token : null, 
    page, 
    rowsPerPage, 
    getCurrentSortConfig().key, 
    getCurrentSortConfig().direction,
    visitedTabs,
    setVisitedTabs,
    `0-2` // Tab identifier for bond ladder
  );

  // Setup WebSocket updates with totalElements setter functions and rowsPerPage
  useWebSocketOrderUpdates(
    setStagingOrderData, 
    setOrderData,
    setStagingOrderTotalElements,
    setOrderTotalElements,
    rowsPerPage
  );

  // Column definitions
  const bondCharColumns = getBondCharColumns(userRole);
  const bondLadderColumns = getBondLadderColumns(userRole);

  // Define allColumnsByTab for version checking
  const allColumnsByTab = {
    '0-0': getOrderColumns(userRole, '0', '0'),
    '0-1': bondCharColumns,
    '0-2': bondLadderColumns,
    '1-0': getOrderColumns(userRole, '1', '0'),
  };

  // Check and update column configurations on component mount
  useEffect(() => {
    // Check and update column configurations for each tab combination
    Object.entries(allColumnsByTab).forEach(([tabKey, columns]) => {
      const sectionKey = `order_${tabKey}`;
      // updateColumnConfigIfNeeded(sectionKey, columns);
      clearConfigIfOutdated(sectionKey, columns);
    });
  }, []);

  // Update selectedColumnsByTab initialization to check localStorage with version management
  const [selectedColumnsByTab, setSelectedColumnsByTab] = useState(() => {
    const defaultColumns = {
      '0-0': getOrderColumns(userRole, '0', '0'),
      '0-1': bondCharColumns,
      '0-2': bondLadderColumns,
      '1-0': getOrderColumns(userRole, '1', '0'),
    };

    // Try to get saved columns for each section using role-based preferences
    Object.keys(defaultColumns).forEach(key => {
      const sectionKey = `order_${key}`;
      
      // Make sure configuration is up to date
      // updateColumnConfigIfNeeded(sectionKey, defaultColumns[key]);
      clearConfigIfOutdated(sectionKey, defaultColumns[key]);
      
      // Then get the saved columns using role-based preferences
      const saved = getPreference(`selectedColumns_${sectionKey}`);
      if (saved) {
        defaultColumns[key] = saved;
      }
    });

    return defaultColumns;
  });

  // State management
  const [oeOpen, setOeOpen] = useState(false);
  const [ccOpen, setCcOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  // Only true when opening OrderEntry from a row click in staging blotter AND user is Trader
  const [qtyPriceEditMode, setQtyPriceEditMode] = useState(false);
  const [cusip, setCusip] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderEntrySource, setOrderEntrySource] = useState("orderBlotter"); // NEW
  const customizeButtonRef = useRef(null);

  const { resetBondLadderData, setBondLadderData: setBondLadderDataZus } = useBondLadderStore();
  const { resetBondCharacteristicData, setBondCharacteristicData: setBondCharacteristicDataZus } = useBondCharacteristicStore();
  const { setActualOrderData: setActualOrderDataZus, resetActualOrderData } = useActualOrderStore();
  // NEW: pull the exact-setter
  const { setActualOrderDataFromApi } = useActualOrderStore.getState
    ? useActualOrderStore.getState()
    : { setActualOrderDataFromApi: () => {} };

  // Effect to reset pagination on tab change
  useEffect(() => {
    setPage(0);
    
    // Reset visited tabs when the page is refreshed
    // This effect will run on initial render only
    const handleBeforeUnload = () => {
      setVisitedTabs(new Set());
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Keep this effect for initialization
  
  // Add a new effect to reset pagination when tabs change
  useEffect(() => {
    setPage(0);
    console.log("visitedTabs", visitedTabs);  
  }, [mainTab, activeTab]);

  // Get total elements based on active tab
  const getTotalElements = () => {
    if (mainTab === 0) {
      // Staging Blotter view with sub-tabs
      if (activeTab === 0) {
        return stagingOrderTotalElements;
      } else if (activeTab === 1) {
        return charTotalElements;
      } else {
        return ladderTotalElements;
      }
    } else {
      // Orders view
      return orderTotalElements;
    }
  };

  // Get loading state based on active tab
  const isLoading = () => {
    if (mainTab === 0) {
      if (activeTab === 0) return loadingStagingOrders;
      if (activeTab === 1) return loadingBondChar;
      return loadingBondLadder;
    }
    return loadingOrders;
  };

  // Data handling
  const activeData = getActiveData(mainTab, activeTab, stagingOrderData, orderData, bondCharData, bondLadderData);

  // Modified handleChangePage to clear the visited tab for the current tab combo
  // This ensures we fetch new data when changing pages
  const handleChangePage = (event, newPage) => {
    // With our new approach, we don't need to clear anything here
    // as the tracking now automatically updates entries by tab ID
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    // With our new approach, we don't need to clear anything here
    // Just remove any existing entries for this tab (clearing is now handled in the hooks)
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Modified pagination display calculation
  const getLabelDisplayedRows = ({ from, to, count }) => {
    // Get the actual number of items we're displaying for current tab
    const currentData = getActiveData(mainTab, activeTab, stagingOrderData, orderData, bondCharData, bondLadderData);
    const actualTo = mainTab === 1
      ? (from === 0 ? 0 : Math.min(to, from + countOrdersBlotterRows(currentData) - 1))
      : Math.min(to, currentData.length);
    
    return `${from.toLocaleString()}–${actualTo.toLocaleString()} of ${count.toLocaleString()}`;
  };

  // Modified handleSort to store sort config for current tab combination and clear visited status
  const handleSort = (columnKey, direction) => {
    // Clear the visited status for the current tab combo when changing sort order
    // This ensures we fetch new data when sorting changes
    setVisitedTabs(prev => {
      const newSet = new Set(prev);
      
      // Clear all entries for current tab-page combinations (for all page numbers)
      Array.from(prev).forEach(entry => {
        if (entry.startsWith(`${mainTab}-${activeTab}`)) {
          newSet.delete(entry);
        }
      });
      
      return newSet;
    });
    
    // Update sort configuration
    setSortConfigs(prev => ({
      ...prev,
      [`${mainTab}-${activeTab}`]: { key: columnKey, direction }
    }));
  };

  // Helper function to get screen name for downloads
  const getScreenName = () => {
    if (mainTab === 0) {
      if (activeTab === 0) return "staging_blotter";
      if (activeTab === 1) return "characteristic";
      if (activeTab === 2) return "ladder";
    }
    return "orders";
  };

  // Event handlers
  const downloadAsCSV = (format = "xlsx") => {
    setCsvLoading(true);
    const tabKey = `${mainTab}-${activeTab}`;
    const sectionKey = `order_${tabKey}`;
    const columns = selectedColumnsByTab[tabKey] || [];

    const savedColumnOrder = getPreference(`columnOrder_${sectionKey}`);
    const orderedKeys = Array.isArray(savedColumnOrder)
      ? savedColumnOrder.map((item) => (typeof item === "string" ? item : item?.key)).filter(Boolean)
      : [];
    const columnMap = new Map(columns.map((col) => [col.key, col]));
    const effectiveColumns = orderedKeys.length
      ? [
          ...orderedKeys.map((key) => columnMap.get(key)).filter(Boolean),
          ...columns.filter((col) => !orderedKeys.includes(col.key)),
        ]
      : columns;

    const sortConfig = getCurrentSortConfig();
    const sortedData = [...(activeData || [])];
    if (sortConfig?.key) {
      sortedData.sort((firstRow, secondRow) => {
        const firstValue = firstRow?.[sortConfig.key];
        const secondValue = secondRow?.[sortConfig.key];

        if (firstValue == null && secondValue == null) return 0;
        if (firstValue == null) return sortConfig.direction === "asc" ? -1 : 1;
        if (secondValue == null) return sortConfig.direction === "asc" ? 1 : -1;

        if (typeof firstValue === "number" && typeof secondValue === "number") {
          return sortConfig.direction === "asc"
            ? firstValue - secondValue
            : secondValue - firstValue;
        }

        const firstString = String(firstValue).toLowerCase();
        const secondString = String(secondValue).toLowerCase();
        if (firstString < secondString) return sortConfig.direction === "asc" ? -1 : 1;
        if (firstString > secondString) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Build headers and rows
    const headers = effectiveColumns.map((col) => col.name);
    const rows = sortedData.map((row) =>
      effectiveColumns.map((col) => {
        let value = row[col.key];
        if (col.key === 'orderExpiryDtTime' && (value === null || value === undefined || value === '')) {
          value = row.rfqExpiryTime ?? value;
        }

        if (value === null || value === undefined) return "";
        if (typeof value === 'object') {
          try { return JSON.stringify(value); } catch (e) { return String(value); }
        }
        return value;
      })
    );

    const aoa = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    
    let blob;
    let extension;
    const screenName = getScreenName();
    if (format === "csv") {
      const csv = XLSX.utils.sheet_to_csv(ws);
      blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      extension = "csv";
    } else {
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      extension = "xlsx";
    }

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${screenName}_data.${extension}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setCsvLoading(false);
  };

  const handleRowClick = (index, rowData) => {
    if ((mainTab === 0 && activeTab === 0) || mainTab === 1) {
      setOrderEntrySource("orderBlotter"); // Reset source
      const normalizedOrder = normalizeOrderForForm(rowData);
      setReadOnly(true);
      setCusip(normalizedOrder.cusip);
      if (typeof setActualOrderDataFromApi === "function") {
        setActualOrderDataFromApi(normalizedOrder);
      } else {
        setActualOrderDataZus(normalizedOrder);
      }
      setSelectedOrder(normalizedOrder);

      // Only enable qty-edit mode when clicking a row in staging blotter AND user is PM.
      // Traders should see pure view-only (no editable qty/price).
      const allowQtyPriceEditForPM = userRole === "PM";
      if (mainTab === 0 && activeTab === 0 && allowQtyPriceEditForPM) {
        setQtyPriceEditMode(true);
      } else {
        setQtyPriceEditMode(false);
      }

      setOeOpen(true);
    }
  };

  // NEW: Handle Allocate Account click
  const handleAllocateAccount = (rowData) => {
    const normalizedOrder = normalizeOrderForForm(rowData);
    setReadOnly(false); // Must be false to allow editing account
    setOrderEntrySource("allocateAccount");
    setCusip(normalizedOrder.cusip);
    if (typeof setActualOrderDataFromApi === "function") {
      setActualOrderDataFromApi(normalizedOrder);
    } else {
      setActualOrderDataZus(normalizedOrder);
    }
    setSelectedOrder(normalizedOrder);
    setQtyPriceEditMode(false); // Ensure other fields are not in edit mode
    setOeOpen(true);
  };

  const handleViewJson = (rowData) => {
    setOrderEntrySource("orderBlotter"); // Reset source
    const normalizedRow = normalizeOrderForForm(rowData);
    setReadOnly(true);
    // We need to preserve the viewMode coming from columnConfig.js.
    // An explicit viewMode wins over the active tab, so a ladder opened from a
    // characteristic row (Ladder Name column) still renders the ladder form.
    const explicitViewMode = rowData.viewMode;
    const isCharacteristicView = explicitViewMode
      ? explicitViewMode === "characteristic"
      : activeTab === 1;
    const isLadderView = explicitViewMode
      ? explicitViewMode === "ladder"
      : activeTab === 2;
    // Only ever explicit: there is no blotter tab whose rows are lists, so a list view is
    // reached solely by clicking a List ID cell.
    const isListView = explicitViewMode === "list";

    if (isListView) {
      // The List RFQ store was already filled by ListIdColumnCell, which fetched the list's
      // bonds before opening this - the same order LadderNameColumnCell uses. Nothing to load
      // here; this only says which form to show and opens it read-only.
      setQtyPriceEditMode(false);
      setSelectedOrder({ ...normalizedRow, viewMode: "list" });
      setOeOpen(true);
    } else if (isCharacteristicView) {
      const flattenedOrder = processBondData(rowData);
      // Preserve viewMode if it exists
      const orderWithViewMode = {
        ...flattenedOrder,
        viewMode: rowData.viewMode || "characteristic"
      };
      resetBondCharacteristicData();
      setBondCharacteristicDataZus(flattenedOrder);
      // Viewing JSON should not enable qty-edit
      setQtyPriceEditMode(false);
      setSelectedOrder(orderWithViewMode);
      setOeOpen(true);
    } else if (isLadderView) {
      const flattenedOrder = processBondData(rowData);
      const updatedFlattendOrder = {
        ...flattenedOrder,
        nameOfTheLadder: flattenedOrder.nameOfTheLadder,
        viewMode: rowData.viewMode || "ladder"
      };
      resetBondLadderData();
      setBondLadderDataZus(updatedFlattendOrder);
      // Viewing JSON should not enable qty-edit
      setQtyPriceEditMode(false);
      setSelectedOrder(updatedFlattendOrder);
      setOeOpen(true);
    } else {
      if (typeof setActualOrderDataFromApi === "function") {
        setActualOrderDataFromApi(normalizedRow);
      } else {
        setActualOrderDataZus(normalizedRow);
      }
      setQtyPriceEditMode(false);
      setSelectedOrder({ ...normalizedRow });
      setOeOpen(true);
    }
  };

  const onOrderAdded = (tab, newOrder) => {
    if (!newOrder) return;

    if (tab === 1) {
      setBondCharData((prev) => {
        const updated = [newOrder, ...prev];
        return updated.slice(0, rowsPerPage);
      });
    } else if (tab === 2) {
      setBondLadderData((prev) => {
        const updated = [newOrder, ...prev];
        return updated.slice(0, rowsPerPage);
      });
    } else if (mainTab === 1) {
      // Orders view - limit to rowsPerPage items
      setOrderData((prev) => {
        const updated = [newOrder, ...prev];
        return updated.slice(0, rowsPerPage); // Ensure we don't exceed rowsPerPage items
      });
      // Update total elements count for pagination
      setOrderTotalElements(prev => prev + 1);
    } else {
      // Staging Blotter view according to sub-tab
      if (tab === 0) {
        setStagingOrderData((prev) => {
          const updated = [newOrder, ...prev];
          return updated.slice(0, rowsPerPage); // Use rowsPerPage instead of hardcoded 20
        });
        setStagingOrderTotalElements(prev => prev + 1);
      }
    }
    setPage(0);
    navigate(`/order?view=${mainTab}&tab=${tab}`, { replace: true });
  };

  // Modified tab change handlers
  const handleMainTabChange = (newMainTab) => {
    setMainTab(newMainTab);
    setActiveTab(0); // Reset sub-tab when main tab changes
    setPage(0); // Reset page number explicitly here too
    navigate(`/order?view=${newMainTab}&tab=0`, { replace: true });
  };

  const handleSubTabChange = (newSubTab) => {
    if (newSubTab >= 0 && ((mainTab === 0 && newSubTab <= 2) || (mainTab === 1 && newSubTab === 0))) {
      setActiveTab(newSubTab);
      setPage(0); // Reset page number explicitly here too
      navigate(`/order?view=${mainTab}&tab=${newSubTab}`, { replace: true });
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResubmitRequest = (event) => {
      const rowData = event.detail;
      if (!rowData) return;
      setOrderEntrySource("orderBlotter"); // Reset source
      const normalizedOrder = normalizeOrderForForm(rowData);

      resetBondCharacteristicData();
      resetBondLadderData();
      setReadOnly(false);
      setQtyPriceEditMode(false);
      // setCusip(normalizedOrder.cusip || "");

      const resubmitOrder = {
        ...normalizedOrder,
        bondLadderId: null,
        characteristicId: null,
        // A reinitiated MA RFQ uses fresh ticket defaults, not the old RFQ's times.
        ...(normalizedOrder.protocol === PROTOCOL_TYPES.MARKETAXESS ? {
          venue: defaultMktxDealers(normalizedOrder.venue, normalizedOrder.mktxDealerListMode),
          effectiveTime: null,
          expiryTime: null,
          rfqExpiryTime: null,
        } : {}),
      };

      if (typeof setActualOrderDataFromApi === "function") {
        setActualOrderDataFromApi(resubmitOrder);
      } else {
        setActualOrderDataZus(resubmitOrder);
      }

      setSelectedOrder(resubmitOrder);
      setOeOpen(true);
    };

    const handleEditRequest = (event) => {
      const rowData = event.detail;
      if (!rowData) return;
      setOrderEntrySource("orderBlotter"); // Reset source
      const normalizedOrder = normalizeOrderForForm(rowData);

      resetBondCharacteristicData();
      resetBondLadderData();
      setReadOnly(false);
      setQtyPriceEditMode(false);
      // setCusip(normalizedOrder.cusip || "");

      const orderWithEditFlag = {
        ...normalizedOrder,
        editRfqMode: true,
        rfqId: rowData?.rfqId || normalizedOrder.rfqId ,
      };

      if (typeof setActualOrderDataFromApi === "function") {
        setActualOrderDataFromApi(orderWithEditFlag);
      } else {
        setActualOrderDataZus(orderWithEditFlag);
      }

      setSelectedOrder(orderWithEditFlag);
      setOeOpen(true);
    };

    window.addEventListener("order:resubmit", handleResubmitRequest);
    window.addEventListener("order:edit", handleEditRequest);
    return () => {
      window.removeEventListener("order:resubmit", handleResubmitRequest);
      window.removeEventListener("order:edit", handleEditRequest);
    };
  }, [
    resetBondCharacteristicData,
    resetBondLadderData,
    setActualOrderDataFromApi,
    setActualOrderDataZus,
    setReadOnly,
    setQtyPriceEditMode,
    setCusip,
    setSelectedOrder,
    setOeOpen,
  ]);

  return (
    <Box sx={{ overflow: "hidden" }}>
      <LoadingOverlay /> {/* Add the loading overlay here */}
      <OrderPageHeader
        activeTab={activeTab}
        setActiveTab={handleSubTabChange}
        mainTab={mainTab}
        setMainTab={handleMainTabChange}
        handleOrderEntryOpen={() => {
          // Viewing a ladder/characteristic order leaves its bondLadderId,
          // bondLadderRungId and characteristicId in the order store; a fresh
          // order must not inherit them
          resetActualOrderData();
          resetBondCharacteristicData();
          resetBondLadderData();
          setReadOnly(false);
          setQtyPriceEditMode(false);
          setOeOpen(true);
        }}
        handleCustomizeColumnsOpen={() => setCcOpen(true)}
        handleDownloadCSV={downloadAsCSV}
        csvLoading={csvLoading}
        customizeButtonRef={customizeButtonRef}
        loadingOrders={loadingOrders}
        loadingStagingOrders={loadingStagingOrders}
        loadingBondChar={loadingBondChar}
        loadingBondLadder={loadingBondLadder}
      />
      <Box sx={{ 
        padding: "0px", 
        height: `calc(100vh - ${mainTab === 0 ? 240 : 200}px)`, 
        overflow: "hidden", 
        display: "flex", 
        flexDirection: "column" 
      }}>
        {isLoading() &&
        (sortConfigs[`${mainTab}-${activeTab}`].key === "updatedAt" || sortConfigs[`${mainTab}-${activeTab}`].key === "bondLadderId"
        || sortConfigs[`${mainTab}-${activeTab}`].key === "characteristicMstId" )
        ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
            <CircularProgress />
          </Box>
        ) : (
          // Use StagingBlotterTable for staging blotter (0-0), BondTable for everything else
          mainTab === 0 && activeTab === 0 ? (
            <StagingBlotterTable
              selectedColumns={selectedColumnsByTab[`${mainTab}-${activeTab}`]}
              handleViewJson={handleViewJson}
              filteredData={activeData}
              handleRowClick={handleRowClick}
              onOrderAdded={onOrderAdded}
              handleCusipClick={handleRowClick}
              handleBuyClick={handleRowClick}
              orderData={stagingOrderData}
              setOrderData={setStagingOrderData}
              sectionKey={`order_${mainTab}-${activeTab}`}
              onSort={handleSort}
              sortConfig={getCurrentSortConfig()}
            />
          ) : (
            <BondTable
              groupListRfqs={mainTab === 1}
              selectedColumns={selectedColumnsByTab[`${mainTab}-${activeTab}`]}
              handleViewJson={handleViewJson}
              filteredData={activeData}
              handleRowClick={handleRowClick}
              onOrderAdded={onOrderAdded}
              handleCusipClick={handleRowClick}
              handleBuyClick={handleRowClick}
              handleAllocateAccount={handleAllocateAccount} // NEW
              orderData={mainTab === 1 ? orderData : stagingOrderData}
              setOrderData={mainTab === 1 ? setOrderData : setStagingOrderData}
              sectionKey={`order_${mainTab}-${activeTab}`}
              onSort={handleSort}
              sortConfig={getCurrentSortConfig()} // Pass current tab's sort config
            />
          )
        )}
      </Box>
      <Box sx={{ position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000, display: "flex", justifyContent: "end" }}>
        <TablePagination
          component="footer"
          sx={{
            ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
          }}
          showFirstButton
          showLastButton
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          page={page}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onPageChange={handleChangePage}
          count={getTotalElements()}
          labelDisplayedRows={getLabelDisplayedRows}
        />
      </Box>
      {oeOpen && (
        <OrderEntry
          readOnly={readOnly}
          open={true} // Always true since we only render when open
          onClose={() => { 
            setCusip(""); 
            setOeOpen(false);
            // Clear selectedOrder and its viewMode when closing the popup
            setSelectedOrder(null);
            // Always clear qty-edit mode when closing
            setQtyPriceEditMode(false);
            setOrderEntrySource("orderBlotter"); // Reset source on close
          }}
          source={orderEntrySource} // CHANGED
          secondarySource={
            selectedOrder?.fromSourceColumn
              ? ORDER_ENTRY_SECONDARY_SOURCES.SOURCE_COLUMN_VIEW
              : undefined
          }
          initialActiveTab={activeTab}
          tabName={
            selectedOrder?.viewMode === "ladder" ? "ladder" : 
            selectedOrder?.viewMode === "characteristic" ? "characteristic" : 
            selectedOrder?.viewMode === "list" ? "list" :
            "empty"
          }
          initialData={selectedOrder || { account: "", bondType: "Treasury", quantity: "100000" }}
          token={token}
          cusip={cusip}
          onOrderAdded={onOrderAdded}
          // qtyPriceEditMode is controlled by the open action (only row-click sets it true)
          qtyPriceEditMode={qtyPriceEditMode}
        />
      )}
      <CustomizeColumns
          open={ccOpen}
          onClose={() => setCcOpen(false)}
          allColumns={allColumnsByTab[`${mainTab}-${activeTab}`]}
          selectedColumns={selectedColumnsByTab[`${mainTab}-${activeTab}`]}
          setSelectedColumnsInventory={(newColumns) =>
              setSelectedColumnsByTab((prev) => ({
                  ...prev,
                  [`${mainTab}-${activeTab}`]: newColumns,
              }))
          }
          anchorEl={customizeButtonRef.current}
          sectionKey={`order_${mainTab}-${activeTab}`}
      />
    </Box>
  );
};

export default OrderPage;
